import { useLogin } from '@/features/IndentityLogin/queries'
import ROUTER from '@/routes/ROUTER'
import IdentityService from '@/shared/services/IdentityService'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Divider } from 'antd'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './LoginScreen.scss'

import ForgetModal from '@/components/common/LoginRelatedComponent/component/Forget/ForgetModal'
import VerifyForgetModal from '@/components/common/LoginRelatedComponent/component/Forget/components/VerifyForgotModal'

import CheckboxCustom from '@/components/ui/ui-react-antd/CheckboxCustom'
import InputCustom from '@/components/ui/ui-react-antd/InputCustom'
import CustomModal from '@/components/ui/ui-react-antd/Modal/CustomModal'
import ButtonCustom from '@/components/ui/ui-react-antd/MyButton/Button'
import { UiForm, UiFormItem } from '@/components/ui/ui-react-antd/UiForm'
import { buildMenuFromTabs, flattenLeaves } from '@/routes/buildMenuFromTabs'
import { useAuth, useIsLogin, useRouterContext } from '@/shared/context'
import queryClient from '@/shared/queries/queryClient'
import { useAppDispatch } from '@/shared/redux/hooks'
import {
  changeAuthorization,
  setListTabs,
  setUserInfo,
} from '@/shared/redux/slices/appGlobal.slice'
import type { LoginRequest } from '@/shared/services/IdentityService/components/identity.param'
import { STORAGE, setAuthToken, setLocalStorage, setRefreshToken } from '@/shared/utils/storage'
import notice from '../../Notice'

const LoginModal = ({ open, onCancel, onLoginSuccess, skipProfileUpdate = false }: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [currentModal, setCurrentModal] = useState('login')

  const [emailForReset, setEmailForReset] = useState('')
  const [_codeVerifyForReset, setCodeVerifyForReset] = useState('')

  const { setIsLogin: setIsLoginContext } = useIsLogin()
  const { setRouterBefore: setRouterBeforeLogin } = useRouterContext()
  const { setUser } = useAuth()

  const navigateAfterLogin = async () => {
    try {
      const resp = await queryClient.fetchQuery({
        queryKey: ['tabs'],
        queryFn: () => IdentityService.getTabs(),
      })
      let fetchedTabs: any[] = []
      if (resp?.isOk) {
        fetchedTabs = [
          { categoryId: 0, Description: 'Tổng Quan', isVisitTab: true },
          { categoryId: -1, codeKey: 'MyTicket', description: 'MyTicket', isVisitTab: true },
          ...(resp.object || []),
        ]
      }
      dispatch(setListTabs(fetchedTabs))
      const menuItems = buildMenuFromTabs(fetchedTabs)
      const startPage = flattenLeaves(menuItems)[0]?.path ?? ROUTER.HOME
      navigate(startPage, { replace: true })
    } catch (error) {
      console.error('Lỗi lấy quyền truy cập:', error)
      navigate(ROUTER.HOME, { replace: true })
    }
  }

  const checkUserNeedsProfileUpdate = useCallback(async () => {
    try {
      const userInfo = await IdentityService.getInfo()
      if (!userInfo || !userInfo.isOk || userInfo.isError) {
        return true
      }
      const userData = userInfo.object
      if (!userData) {
        return true
      }
      return userData.isDetailRequired
    } catch (error) {
      console.error('Error checking user profile:', error)
      return true
    }
  }, [])

  const handleLoginSuccess = useCallback(
    async (userInfo: any, isFirstLogin = false) => {
      handleCloseAllModals()
      if (onLoginSuccess) {
        onLoginSuccess(userInfo)
      }
      if (skipProfileUpdate) {
        navigate(ROUTER.HOME, { replace: true })
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
        return
      }
      try {
        const needsProfileUpdate = (await checkUserNeedsProfileUpdate()) || isFirstLogin

        if (needsProfileUpdate) {
          navigate('/cap-nhat-thong-tin', { replace: true })
        } else {
          await navigateAfterLogin()
          return // prevent second notice because navigateAfterLogin already redirects
        }
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
      } catch (error) {
        console.error('Error in handleLoginSuccess:', error)
        await navigateAfterLogin()
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
      }
    },
    [
      onLoginSuccess,
      skipProfileUpdate,
      navigate,
      checkUserNeedsProfileUpdate, // Now stable
    ]
  )

  const handleRegister = (type: string) => {
    handleCloseAllModals()
    // Có thể truyền type để phân biệt cán bộ/nhân viên
    if (type === 'canbo') {
      navigate(ROUTER.DANG_KY_RA_VAO, { state: { accountType: type } })
    } else if (type === 'doanhnghiep') {
      navigate(ROUTER.DANG_KY_DOANH_NGHIEP_WIZARD)
    } else {
      navigate(ROUTER.DANG_KY_KHACH, { state: { accountType: type } })
    }
  }

  const fetchUserInfo = useCallback(
    async (token: string) => {
      try {
        const response = await IdentityService.getInfo()
        if (response?.isOk) {
          const userInfo = response?.object
          setLocalStorage(STORAGE.USER_INFO, userInfo)
          setUser(userInfo)
          return userInfo
        }
        throw new Error('Failed to fetch user info')
      } catch (error) {
        console.error('Error fetching user info:', error)
        const basicUserInfo = {
          accessToken: token,
          Token: token,
          isProfileCompleted: false,
        }
        setLocalStorage(STORAGE.USER_INFO, basicUserInfo)
        setUser(basicUserInfo)
        return basicUserInfo
      }
    },
    [setUser]
  )

  const { mutate: login, isPending: isLoginPending } = useLogin()

  const onFinish = (values: LoginRequest) => {
    login(values, {
      onSuccess: async (userInfo) => {
        handleCloseAllModals()
        // Thiếu các dòng này (setIsLoginContext/setUser/dispatch) khiến DefaultAction không
        // re-render lại để gọi getTabs() sau khi đăng nhập trong SPA (chỉ chạy đúng lúc app
        // mount lần đầu với token đã có sẵn) → listTabs rỗng → 403 toàn bộ admin. Đối chiếu
        // hành động sau đăng nhập của qlrv (app/fe/admin/.../LoginModal.tsx onFinish).
        setUser(userInfo)
        dispatch(setUserInfo(userInfo))
        dispatch(changeAuthorization(true))
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
        await navigateAfterLogin()
      },
      onError: (error: Error) => {
        notice({
          msg: error.message || 'Đăng nhập thất bại',
          isSuccess: false,
        })
      },
    })
  }

  const handleOpenForgetPassModal = (e: React.MouseEvent) => {
    e.preventDefault()
    setCurrentModal('forget')
  }

  const handleOpenVerifyModal = () => {
    setCurrentModal('verify')
  }

  const handleOpenRePasswordModal = () => {
    setCurrentModal('repassword')
  }

  const handleBackToLoginFromReset = () => {
    setCurrentModal('login')
    setEmailForReset('')
    setCodeVerifyForReset('')
  }

  const handleCloseAllModals = () => {
    setCurrentModal('login')
    setEmailForReset('')
    setCodeVerifyForReset('')
    onCancel()
  }

  const handleCancelForgetPassModal = () => {
    handleBackToLoginFromReset()
  }

  const handleCancelVerifyModal = () => {
    setCurrentModal('forget')
  }

  useEffect(() => {
    if (open) {
      setCurrentModal('login')
    }
  }, [open])

  useEffect(() => {
    const handleOAuthRedirect = async () => {
      const hash = location.hash
      if (!hash) {
        return
      }

      const params = new URLSearchParams(hash.substring(1))
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')
      const userDetailRequired = params.get('user_detail_required')

      if (accessToken) {
        try {
          setLoading(true)

          // OAuth redirect không có checkbox "Ghi nhớ", mặc định remember=true (localStorage)
          // để khớp với cách use-login.ts lưu token, tránh getAuthToken()/getRefreshToken()
          // decrypt sai và làm refresh token luôn fail sau khi hết hạn access token.
          const remember = true
          setLocalStorage(STORAGE.REMEMBER_HOME, remember)
          setAuthToken(accessToken, remember)
          if (refreshToken) {
            setRefreshToken(refreshToken, remember)
          }
          setIsLoginContext(accessToken)
          setRouterBeforeLogin('')

          let userInfo: any
          let isFirstLogin = false

          if (userDetailRequired === 'True') {
            userInfo = await fetchUserInfo(accessToken)
            isFirstLogin = true
          } else {
            userInfo = await fetchUserInfo(accessToken)

            // Kiểm tra xem có cần cập nhật profile không
            const needsProfileUpdate = await checkUserNeedsProfileUpdate()
            isFirstLogin = !!needsProfileUpdate
          }

          // Lưu thông tin user
          setLocalStorage(STORAGE.USER_INFO, userInfo)
          setUser(userInfo)
          dispatch(setUserInfo(userInfo))
          dispatch(changeAuthorization(true))

          window.location.hash = ''

          // Gọi handleLoginSuccess
          await handleLoginSuccess(userInfo, isFirstLogin)
        } catch (error) {
          console.error('❌ Error processing OAuth redirect:', error)
        } finally {
          setLoading(false)
        }
      } else {
      }
    }

    handleOAuthRedirect()
  }, [
    location.hash,
    setIsLoginContext,
    setRouterBeforeLogin,
    setUser,
    dispatch,
    fetchUserInfo,
    checkUserNeedsProfileUpdate,
    handleLoginSuccess,
  ])

  return (
    <>
      <CustomModal
        open={open && currentModal === 'login'}
        onCancel={handleCloseAllModals}
        footer={null}
        centered
        width={500}
        className="login-modal-custom"
      >
        <div className="login-form-wrapper">
          <div className="login-form">
            <h2 className="login-title">Đăng nhập hệ thống</h2>

            <UiForm layout="vertical" onFinish={onFinish} disabled={isLoginPending || loading}>
              <UiFormItem
                label={
                  <span style={{ color: '#333', fontSize: '14px', fontWeight: 600 }}>
                    Tài khoản
                  </span>
                }
                name="username"
                className="mb-8"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập tài khoản!',
                  },
                  // {
                  //   // pattern: getRegexEmail(),
                  //   message: 'Email không hợp lệ',
                  // },
                ]}
              >
                <InputCustom
                  size="large"
                  prefix={<UserOutlined />}
                  placeholder="Nhập tên tài khoản"
                />
              </UiFormItem>

              <UiFormItem
                label={
                  <span style={{ color: '#333', fontSize: '14px', fontWeight: 600 }}>Mật khẩu</span>
                }
                name="password"
                className="mb-8"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu!' },
                  { min: 6, message: 'Mật khẩu ít nhất 6 ký tự!' },
                ]}
              >
                <InputCustom
                  type="password"
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Nhập mật khẩu"
                />
              </UiFormItem>

              <div className="login-options">
                <UiFormItem name="remember" valuePropName="checked" noStyle>
                  <CheckboxCustom>Ghi nhớ mật khẩu</CheckboxCustom>
                </UiFormItem>
                <ButtonCustom
                  type="link"
                  variant="link"
                  className="forgot-password"
                  style={{
                    padding: 0,
                    height: 'auto',
                    color: '#4285f4',
                    fontSize: '14px',
                  }}
                  onClick={handleOpenForgetPassModal}
                >
                  Quên mật khẩu?
                </ButtonCustom>
              </div>

              <UiFormItem>
                <ButtonCustom
                  htmlType="submit"
                  size="large"
                  className="login-button-primary"
                  loading={isLoginPending || loading}
                >
                  Đăng nhập
                </ButtonCustom>
              </UiFormItem>
            </UiForm>

            {/* <div style={{ width: "100%", padding: "0 10px" }}> */}
            <Divider
              plain
              style={{
                margin: '20px 0',
                color: '#999',
                fontSize: '12px',
                textTransform: 'uppercase',
                borderColor: '#e8e8e8',
              }}
            >
              Bạn chưa có tài khoản?
            </Divider>

            {/* <FormLoginSocial /> */}
            {/* </div> */}

            {/* Phần đăng ký tài khoản trong form */}
            <div
              style={{
                textAlign: 'center',
                color: '#666',
                fontSize: '14px',
              }}
            >
              <div className="registration">
                <div className="registration__links">
                  {/* <div>
                    <ButtonCustom
                      type="link"
                      variant="link"
                      className="registration__link registration__link--member"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        handleRegister('canbo')
                      }}
                    >
                      Đăng ký tài khoản cán bộ
                    </ButtonCustom>
                  </div>
                  <div>Hoặc</div>
                  <div>
                    <ButtonCustom
                      type="link"
                      variant="link"
                      className="registration__link registration__link--staff"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        handleRegister('khach')
                      }}
                    >
                      Đăng ký khách hàng
                    </ButtonCustom>
                  </div> */}
                  {/* <div>Hoặc</div> */}
                  <div>
                    <ButtonCustom
                      type="link"
                      variant="link"
                      className="registration__link registration__link--enterprise"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        handleRegister('doanhnghiep')
                      }}
                    >
                      Đăng ký doanh nghiệp
                    </ButtonCustom>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <ForgetModal
        openForgetPassModal={open && currentModal === 'forget'}
        handleCancel={handleCancelForgetPassModal}
        handleLogin={handleBackToLoginFromReset}
        setOpenVerifyModal={handleOpenVerifyModal}
        setEmail={setEmailForReset}
      />

      <VerifyForgetModal
        openVerifyModal={open && currentModal === 'verify'}
        handleCancel={handleCancelVerifyModal}
        handleLogin={handleBackToLoginFromReset}
        setRePasswordModal={handleOpenRePasswordModal}
        email={emailForReset}
        setCodeVerify={setCodeVerifyForReset}
      />
    </>
  )
}

export default LoginModal
